import { useNotificationToast } from '@context/NotificationToastContext'
import {
  CallFlowBranchKey,
  CallFlowStepType,
  ICallFlow,
  ICallFlowDefinition,
  ICallFlowStep,
  IClinicInternalExtension,
  IClinicNumber,
} from '@dataTypes/clinic.type'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Dispatch,
  MutableRefObject,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FlowEditorProps } from './FlowEditor.component'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import {
  clinicNumberHasWorkingHoursSetup,
  clinicWorkingHoursDataEqual,
} from '@utils/phoneManagement.utils'
import {
  createCallFlowAction,
  fetchCallFlowsAction,
  fetchPhoneManagementData,
  updateCallFlowAction,
} from '@store/actions/settings-communication.action'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  deleteBlobPrefix,
  generateReadOnlyUrl,
  generateUploadUrl,
  uploadFile,
} from '@services/api/clinic.api'
import { BLOB_FOLDERS } from '@constants/Azure'
import { smsMessagePair } from '@utils/communicationFlow.util'

const defaultDefinition: ICallFlowDefinition = {
  version: 1,
  numbers: [],
  numberSettings: {},
  branches: {
    work: { steps: [] },
    lunch: { steps: [] },
    closed: { steps: [] },
  },
}

const defaultFlow: ICallFlow = {
  clinic_id: '',
  name: 'New flow',
  status: 'draft',
  flow_type: 'call',
  definition: defaultDefinition,
}

const LOADING_CONTAINER = 'settings-call-flow-editor'
const CALL_FLOW_AUDIO_FOLDER = BLOB_FOLDERS.CALL_FLOW_AUDIO

const createStep = (type: CallFlowStepType): ICallFlowStep => {
  const id = `step-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  switch (type) {
    case 'play_audio':
      return { id, type: 'play_audio' }
    case 'menu_ivr':
      return { id, type: 'menu_ivr' }
    case 'dial_number':
      return { id, type: 'dial_number' }
    case 'add_queue':
      return { id, type: 'add_queue' }
    case 'route_call':
    case 'reroute_call':
      return { id, type: 'route_call' }
    case 'voicemail':
      return { id, type: 'voicemail' }
    case 'hang_up':
      return { id, type: 'hang_up' }
    case 'no_action':
      return { id, type: 'no_action' }
    case 'ai_receptionist':
      return { id, type: 'ai_receptionist' }
    case 'send_sms':
      return { id, type: 'send_sms' }
    case 'route_sms':
      return { id, type: 'route_sms' }
    default: {
      const _exhaustive: never = type
      return _exhaustive
    }
  }
}

const asRouteCall = (step: ICallFlowStep): ICallFlowStep => {
  if (step.type === 'reroute_call') return { ...step, type: 'route_call' }
  return step
}

const ensureBranches = (
  def: ICallFlowDefinition,
): Record<CallFlowBranchKey, { steps: ICallFlowStep[] }> => {
  const mapSteps = (steps: ICallFlowStep[] | undefined): ICallFlowStep[] =>
    (steps ?? []).map(asRouteCall)
  return {
    work: { steps: mapSteps(def.branches?.work?.steps) },
    lunch: { steps: mapSteps(def.branches?.lunch?.steps) },
    closed: { steps: mapSteps(def.branches?.closed?.steps) },
  }
}

const BRANCH_KEYS: CallFlowBranchKey[] = ['work', 'lunch', 'closed']

const stripSmsMessages = (def: ICallFlowDefinition): ICallFlowDefinition => {
  const branches = ensureBranches(def)
  const next: ICallFlowDefinition = { ...def, branches: { ...branches } }
  for (const key of BRANCH_KEYS) {
    next.branches[key] = {
      steps: (branches[key]?.steps ?? []).map((step) => {
        if (step.type === 'send_sms') {
          const pair = smsMessagePair(step.messageHtml, step.message)
          return {
            ...step,
            messageHtml: pair.messageHtml,
            message: pair.message || undefined,
          }
        }
        if (step.type === 'route_sms') {
          if (step.ifNoReply !== 'send_sms') {
            return { ...step, messageHtml: undefined, message: undefined }
          }
          const pair = smsMessagePair(step.messageHtml, step.message)
          return {
            ...step,
            messageHtml: pair.messageHtml,
            message: pair.message || undefined,
          }
        }
        return step
      }),
    }
  }
  return next
}

const flowToPersistDefinition = (
  def: ICallFlowDefinition,
): Record<string, unknown> => {
  return JSON.parse(JSON.stringify(def)) as Record<string, unknown>
}

const resolveFlowClinicNumberIds = (
  flow: ICallFlow | null | undefined,
): string[] => {
  if (!flow) return []
  const fromLinks = (flow.number_links ?? [])
    .map((l) => l.clinic_number_id)
    .filter((id): id is string => Boolean(id))
  if (fromLinks.length > 0) return fromLinks
  return flow.definition?.numbers ?? []
}

const resolveSelectedNumbersFromFlow = (
  flow: ICallFlow | null | undefined,
  clinicNumbers: IClinicNumber[],
): IClinicNumber[] =>
  resolveFlowClinicNumberIds(flow)
    .map((id) => clinicNumbers.find((n) => n.clinic_number_id === id) ?? null)
    .filter(Boolean) as IClinicNumber[]

const SAFE_SEG_RE = /[^a-zA-Z0-9._-]+/g
const safeBlobSegment = (s: string): string =>
  String(s).replace(SAFE_SEG_RE, '_').slice(0, 120)

async function uploadOneCallFlowAudio(
  file: File,
  clinicId: string,
  flowKey: string,
  stepId: string,
): Promise<{ url: string; filename: string; fileSizeBytes: number }> {
  const ext =
    (file.name.match(/\.[a-zA-Z0-9]+$/)?.[0] ?? '').toLowerCase() || '.mp3'
  const baseName = safeBlobSegment(
    file.name.replace(/\.[a-zA-Z0-9]+$/, '') || 'audio',
  )
  const filename = `${baseName}-${Date.now()}${ext.startsWith('.') ? ext : `.${ext}`}`
  const ct = file.type || 'application/octet-stream'
  const up = await generateUploadUrl(ct, filename, {
    folder: CALL_FLOW_AUDIO_FOLDER,
    clinic_id: clinicId,
    extra_path: safeBlobSegment(flowKey),
  })
  if (up.statusCode !== 200 || !up.data?.upload_url) {
    throw new Error(up.message || 'Could not get upload URL')
  }
  await uploadFile(up.data.upload_url, file)
  const blobRef = up.data.blob_name || filename
  const ro = await generateReadOnlyUrl(blobRef, 24 * 365)
  if (ro.statusCode !== 200 || !ro.data?.read_only_url) {
    throw new Error(ro.message || 'Could not get read URL')
  }
  return {
    url: ro.data.read_only_url,
    filename: blobRef,
    fileSizeBytes: file.size,
  }
}

async function processStepForAudioUpload(
  step: ICallFlowStep,
  pending: Map<string, File>,
  clinicId: string,
  flowKey: string,
): Promise<ICallFlowStep> {
  const f = pending.get(step.id)
  if (step.type === 'play_audio' && f) {
    const {
      url: audioUrl,
      filename,
      fileSizeBytes,
    } = await uploadOneCallFlowAudio(f, clinicId, flowKey, step.id)
    pending.delete(step.id)
    return {
      ...step,
      audioUrl,
      fileName: filename,
      fileSizeBytes,
    }
  }
  if (step.type === 'voicemail' && f) {
    const {
      url: audioUrl,
      filename: filename,
      fileSizeBytes,
    } = await uploadOneCallFlowAudio(f, clinicId, flowKey, step.id)
    pending.delete(step.id)
    return {
      ...step,
      greetingAudioUrl: audioUrl,
      greetingFileName: filename,
      greetingFileSizeBytes: fileSizeBytes,
    }
  }
  if (step.type === 'add_queue' && f && step.holdMusicId === 'custom') {
    const {
      url: audioUrl,
      filename: filename,
      fileSizeBytes,
    } = await uploadOneCallFlowAudio(f, clinicId, flowKey, step.id)
    pending.delete(step.id)
    return {
      ...step,
      holdMusicCustomAudioUrl: audioUrl,
      holdMusicCustomFileName: filename,
      holdMusicCustomFileSizeBytes: fileSizeBytes,
    }
  }
  if (step.type === 'dial_number') {
    let answeredFollowStep = step.answeredFollowStep
    let noAnswerFollowStep = step.noAnswerFollowStep
    if (answeredFollowStep) {
      answeredFollowStep = await processStepForAudioUpload(
        answeredFollowStep,
        pending,
        clinicId,
        flowKey,
      )
    }
    if (noAnswerFollowStep) {
      noAnswerFollowStep = await processStepForAudioUpload(
        noAnswerFollowStep,
        pending,
        clinicId,
        flowKey,
      )
    }
    return { ...step, answeredFollowStep, noAnswerFollowStep }
  }
  return step
}

async function uploadPendingAudioInDefinition(
  definition: ICallFlowDefinition,
  pending: Map<string, File>,
  clinicId: string,
  flowKey: string,
): Promise<ICallFlowDefinition> {
  const branches = ensureBranches(definition)
  const next: ICallFlowDefinition = {
    ...definition,
    branches: { ...branches },
  }
  for (const key of BRANCH_KEYS) {
    const list = branches[key]?.steps ?? []
    const steps: ICallFlowStep[] = []
    for (const s of list) {
      steps.push(await processStepForAudioUpload(s, pending, clinicId, flowKey))
    }
    next.branches[key] = { steps }
  }
  return next
}

const isUnsetOrNA = (v: string | null | undefined): boolean =>
  v == null || String(v).trim() === '' || String(v).trim() === 'N/A'

const isRouteOrRerouteCallFormComplete = (
  step:
    | Extract<ICallFlowStep, { type: 'route_call' }>
    | Extract<ICallFlowStep, { type: 'reroute_call' }>,
): boolean => {
  if (!step.team?.trim()) return false
  if (!step.ringType?.trim()) return false
  if (
    step.ringDurationSec == null ||
    Number.isNaN(step.ringDurationSec) ||
    step.ringDurationSec <= 0
  ) {
    return false
  }
  if (!step.ifNoAnswer?.trim()) return false
  if (step.ifNoAnswer === 'voicemail') {
    if (step.voicemailAllowRerecord == null) return false
    if (step.voicemailAllowRerecord) {
      if (
        step.voicemailMaxRecordingSec == null ||
        Number.isNaN(step.voicemailMaxRecordingSec) ||
        step.voicemailMaxRecordingSec <= 0
      ) {
        return false
      }
      if (step.includeVoicemailTranscription == null) return false
    }
  }
  if (step.ifNoAnswer === 'tts') {
    if (!step.ifNoAnswerTtsText?.trim()) return false
  }
  return true
}

const isCallFlowStepComplete = (step: ICallFlowStep): boolean => {
  switch (step.type) {
    case 'hang_up':
    case 'no_action':
      return true
    case 'play_audio':
      return Boolean(step.fileName?.trim() || step.audioUrl?.trim())
    case 'menu_ivr': {
      const t = step.ttsText?.trim()
      const keys = step.keys ?? []
      if (!t || keys.length === 0) return false
      return keys.every(
        (k) => Boolean(k.digit?.trim()) && Boolean(k.action?.trim()),
      )
    }
    case 'dial_number': {
      if (isUnsetOrNA(step.phoneNumber) && isUnsetOrNA(step.extension)) {
        return false
      }
      if (
        !step.answeredFollowStep ||
        (step.answeredFollowStep &&
          !isCallFlowStepComplete(step.answeredFollowStep))
      ) {
        return false
      }
      if (
        !step.noAnswerFollowStep ||
        (step.noAnswerFollowStep &&
          !isCallFlowStepComplete(step.noAnswerFollowStep))
      ) {
        return false
      }
      return true
    }
    case 'add_queue': {
      if (!step.team?.trim()) return false
      if (step.informPosition == null) return false
      if (!step.holdMusicId?.trim()) return false
      if (!step.onTimeout?.trim()) return false
      if (
        step.maxWaitSec == null ||
        Number.isNaN(step.maxWaitSec) ||
        step.maxWaitSec <= 0
      ) {
        return false
      }
      if (step.holdMusicId === 'custom') {
        if (!step.holdMusicCustomAudioUrl?.trim()) return false
      }
      if (step.onTimeout === 'reroute') {
        if (
          isUnsetOrNA(step.rerouteNumber) &&
          isUnsetOrNA(step.rerouteExtension)
        ) {
          return false
        }
      }
      return true
    }
    case 'route_call':
      return isRouteOrRerouteCallFormComplete(step)
    case 'reroute_call':
      return isRouteOrRerouteCallFormComplete(step)
    case 'ai_receptionist':
      return true
    case 'send_sms':
      return Boolean(smsMessagePair(step.messageHtml, step.message).message)
    case 'route_sms': {
      if (!step.team?.trim()) return false
      if (
        step.timeoutMinutes == null ||
        Number.isNaN(step.timeoutMinutes) ||
        step.timeoutMinutes <= 0
      ) {
        return false
      }
      if (
        step.ifNoReply !== 'send_sms' &&
        step.ifNoReply !== 'ai_receptionist'
      ) {
        return false
      }
      if (
        step.ifNoReply === 'send_sms' &&
        !smsMessagePair(step.messageHtml, step.message).message
      ) {
        return false
      }
      return true
    }
    case 'voicemail': {
      const hasGreeting = Boolean(
        step.greetingFileName?.trim() || step.greetingAudioUrl?.trim(),
      )
      if (!hasGreeting) return false
      if (
        step.maxRecordingSec == null ||
        Number.isNaN(step.maxRecordingSec) ||
        step.maxRecordingSec <= 0
      ) {
        return false
      }
      if (step.voicemailAllowRerecord == null) return false
      if (step.voicemailAllowRerecord) {
        if (step.includeVoicemailTranscription == null) return false
      }
      if (!step.afterRecording?.trim()) return false
      return true
    }
    default: {
      const _e: never = step
      return _e
    }
  }
}

const callFlowDefinitionFullyConfigured = (
  def: ICallFlowDefinition,
): boolean => {
  const branches = ensureBranches(def)
  return BRANCH_KEYS.every((key) => {
    const steps = branches[key]?.steps ?? []
    if (steps.length === 0) {
      return false
    }
    if (
      !steps.every((s) => {
        return isCallFlowStepComplete(s)
      })
    ) {
      return false
    }
    return true
  })
}

const parseDefinition = (raw: unknown): ICallFlowDefinition => {
  if (!raw || typeof raw !== 'object') {
    return defaultDefinition
  }
  const o = raw as Record<string, unknown>
  const base = defaultDefinition
  return {
    version: typeof o.version === 'number' ? o.version : 1,
    numbers: Array.isArray(o.numbers) ? (o.numbers as string[]) : [],
    numberSettings:
      o.numberSettings && typeof o.numberSettings === 'object'
        ? (o.numberSettings as ICallFlowDefinition['numberSettings'])
        : {},
    branches: ensureBranches({
      ...base,
      branches:
        (o.branches as ICallFlowDefinition['branches']) ?? base.branches,
    }),
  }
}

export type DialFollowOutcome = 'answered' | 'no_answer'

export type PickerDialContext = {
  branch: CallFlowBranchKey
  dialStepId: string
  outcome: DialFollowOutcome
}

export const dialOutcomeAnchorRefKey = (
  branch: CallFlowBranchKey,
  dialStepId: string,
  outcome: DialFollowOutcome,
): string => `${branch}:${dialStepId}:${outcome}`

export type FlowEditorEditingStep = {
  branch: CallFlowBranchKey
  stepId: string
  dialParentStepId?: string
  dialOutcome?: DialFollowOutcome
}

interface IFlowEditorHooks {
  activeNumber: IClinicNumber | null
  addStepBtnRefs: MutableRefObject<
    Record<CallFlowBranchKey, HTMLElement | null>
  >
  dialOutcomeBtnRefs: MutableRefObject<Record<string, HTMLElement | null>>
  addStepToBranch: (type: CallFlowStepType) => void
  branches: Record<CallFlowBranchKey, { steps: ICallFlowStep[] }>
  callFlow: ICallFlow
  cancelEditName: () => void
  cancelWorkingHours: () => void
  closeStepPanel: () => void
  clinicNumbers: IClinicNumber[]
  internalExtensions: IClinicInternalExtension[]
  closeStepPicker: () => void
  diagramWrapRef: RefObject<HTMLDivElement>
  editName: boolean
  editingStep: FlowEditorEditingStep | null
  goToNextWorkingHoursNumber: () => void
  hasChanges: boolean
  handleSelectNumber: (e: { checked: boolean }, number: IClinicNumber) => void
  numberReassignConfirm: {
    number: IClinicNumber
    otherFlowName: string
  } | null
  confirmNumberReassign: () => void
  cancelNumberReassign: () => void
  numberListCheckboxRemountKey: Record<string, number>
  numberListFlyoutRef: RefObject<HTMLDivElement>
  numberListOpen: boolean
  onAddStep: (branch: CallFlowBranchKey) => void
  onAddDialOutcomeStep: (
    branch: CallFlowBranchKey,
    dialStepId: string,
    outcome: DialFollowOutcome,
  ) => void
  pickerDialContext: PickerDialContext | null
  onClinicNumbersSaved: (updated: IClinicNumber[]) => void
  removeStepFromBranch: (branch: CallFlowBranchKey, stepId: string) => void
  openStepPanel: (branch: CallFlowBranchKey, stepId: string) => void
  pickerOpen: boolean
  pickerPos: {
    top: number
    left: number
    placement: 'above' | 'below'
  } | null
  resetDraft: () => void
  saveFlow: () => Promise<void>
  registerPendingAudioFile: (stepId: string, file: File | null) => void
  saveName: () => Promise<void>
  selectedNumbers: IClinicNumber[]
  setActiveNumber: Dispatch<SetStateAction<IClinicNumber | null>>
  setCallFlow: Dispatch<SetStateAction<ICallFlow>>
  setEditName: Dispatch<SetStateAction<boolean>>
  setEditingStep: Dispatch<SetStateAction<FlowEditorEditingStep | null>>
  setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
  setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
  setNumberListOpen: Dispatch<SetStateAction<boolean>>
  showCallFlowBranches: boolean
  updateActiveNumber: (
    data: Partial<IClinicNumber & { timezone?: string }>,
  ) => void
  updateStep: (
    branch: CallFlowBranchKey,
    stepId: string,
    patch: Partial<ICallFlowStep>,
  ) => void
  workingHoursDataDirty: boolean
  workingHoursPhoneLabel: string
}

const useFlowEditor = (props: FlowEditorProps): IFlowEditorHooks => {
  const dispatch = useAppDispatch()
  const { initialFlow, cancelEditor } = props
  const clinicNumbers = useAppSelector(
    (state) => state.settingsCommunication.clinicNumbers,
  )
  const internalExtensions = useAppSelector(
    (state) => state.settingsCommunication.internalExtensions,
  )
  const callFlows = useAppSelector(
    (state) => state.settingsCommunication.callFlows,
  )
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [callFlow, setCallFlow] = useState<ICallFlow>(
    initialFlow ?? defaultFlow,
  )
  const [baselineName, setBaselineName] = useState(
    initialFlow?.name ?? defaultFlow.name,
  )
  const [editName, setEditName] = useState(false)
  const [numberListOpen, setNumberListOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerBranch, setPickerBranch] = useState<CallFlowBranchKey | null>(
    null,
  )
  const [selectedNumbers, setSelectedNumbers] = useState<IClinicNumber[]>(() =>
    resolveSelectedNumbersFromFlow(initialFlow, clinicNumbers),
  )
  const [activeNumber, setActiveNumber] = useState<IClinicNumber | null>(null)
  const [editingStep, setEditingStep] = useState<FlowEditorEditingStep | null>(
    null,
  )
  const [numberReassignConfirm, setNumberReassignConfirm] = useState<{
    number: IClinicNumber
    otherFlowName: string
  } | null>(null)
  const [numberListCheckboxRemountKey, setNumberListCheckboxRemountKey] =
    useState<Record<string, number>>({})
  const numberReassignConfirmRef = useRef<{
    number: IClinicNumber
    otherFlowName: string
  } | null>(null)
  numberReassignConfirmRef.current = numberReassignConfirm
  const diagramWrapRef = useRef<HTMLDivElement>(null)
  const pendingAudioByStepIdRef = useRef<Map<string, File>>(new Map())
  const [pendingAudioRevision, setPendingAudioRevision] = useState(0)
  const numberListFlyoutRef = useRef<HTMLDivElement>(null)
  const addStepBtnRefs = useRef<Record<CallFlowBranchKey, HTMLElement | null>>({
    work: null,
    lunch: null,
    closed: null,
  })
  const dialOutcomeBtnRefs = useRef<Record<string, HTMLElement | null>>({})
  const [pickerDialContext, setPickerDialContext] =
    useState<PickerDialContext | null>(null)
  const [pickerPos, setPickerPos] = useState<{
    top: number
    left: number
    placement: 'above' | 'below'
  } | null>(null)

  const branches = useMemo(
    () => ensureBranches(callFlow.definition),
    [callFlow.definition],
  )

  useEffect(() => {
    if (!editingStep) return
    const list = callFlow.definition.branches[editingStep.branch]?.steps ?? []
    if (editingStep.dialParentStepId && editingStep.dialOutcome) {
      const parent = list.find((s) => s.id === editingStep.dialParentStepId)
      if (!parent || parent.type !== 'dial_number') {
        setEditingStep(null)
        return
      }
      const dial = parent
      const child =
        editingStep.dialOutcome === 'answered'
          ? dial.answeredFollowStep
          : dial.noAnswerFollowStep
      if (!child || child.id !== editingStep.stepId) {
        setEditingStep(null)
      }
      return
    }
    if (!list.some((s) => s.id === editingStep.stepId)) {
      setEditingStep(null)
    }
  }, [editingStep, callFlow.definition.branches])

  const workingHoursPhoneLabel = useMemo(() => {
    const line = clinicNumbers.find(
      (n) => n.clinic_number_id === activeNumber?.clinic_number_id,
    )
    return line ? formatPhoneNumber(line.phone_number) : ''
  }, [clinicNumbers, activeNumber])

  const goToNextWorkingHoursNumber = useCallback(() => {
    const ids = selectedNumbers.map((n) => n.clinic_number_id!)
    if (!activeNumber) return
    const i = ids.indexOf(activeNumber.clinic_number_id!)
    if (i < 0 || i >= ids.length - 1) return
    setEditingStep(null)
    setActiveNumber(selectedNumbers[i + 1])
  }, [selectedNumbers, activeNumber])

  const workingHoursDataDirty = useMemo(
    () =>
      activeNumber
        ? selectedNumbers.some((n) => {
            const baseline = clinicNumbers.find(
              (c) => c.clinic_number_id === n.clinic_number_id,
            )
            if (!baseline) return true
            return !clinicWorkingHoursDataEqual(n, baseline)
          })
        : false,
    [activeNumber, selectedNumbers, clinicNumbers],
  )

  const baselineDefinition = useMemo(
    () =>
      initialFlow
        ? parseDefinition(initialFlow.definition as unknown)
        : defaultDefinition,
    [initialFlow],
  )

  const baselineNumberIds = useMemo(
    () => [...resolveFlowClinicNumberIds(initialFlow)].sort(),
    [initialFlow],
  )

  const hasChanges = useMemo(() => {
    if (callFlow.name !== baselineName) return true

    const numbersWorkingHoursDirty = selectedNumbers.some((n) => {
      const baseline = clinicNumbers.find(
        (c) => c.clinic_number_id === n.clinic_number_id,
      )
      if (!baseline) return true
      return !clinicWorkingHoursDataEqual(n, baseline)
    })
    if (numbersWorkingHoursDirty) return true

    if (pendingAudioByStepIdRef.current.size > 0) return true

    const currentIds = selectedNumbers.map((n) => n.clinic_number_id!).sort()
    if (
      currentIds.length !== baselineNumberIds.length ||
      !currentIds.every((id, i) => id === baselineNumberIds[i])
    ) {
      return true
    }

    const currentDef = JSON.stringify(
      flowToPersistDefinition(callFlow.definition),
    )
    const savedDef = JSON.stringify(flowToPersistDefinition(baselineDefinition))
    return currentDef !== savedDef
  }, [
    callFlow.name,
    callFlow.definition,
    baselineName,
    baselineDefinition,
    baselineNumberIds,
    selectedNumbers,
    clinicNumbers,
    pendingAudioRevision,
  ])

  const showCallFlowBranches = useMemo(
    () =>
      selectedNumbers.length > 0 &&
      selectedNumbers.every((n) => {
        const saved = clinicNumbers.find(
          (c) => c.clinic_number_id === n.clinic_number_id,
        )
        return clinicNumberHasWorkingHoursSetup(saved)
      }),
    [selectedNumbers, clinicNumbers],
  )

  const cancelWorkingHours = useCallback(() => {
    if (!activeNumber) return
    setEditingStep(null)
    const restoredNumbers = selectedNumbers.map((n) =>
      clinicNumbers.find((cn) => cn.clinic_number_id === n.clinic_number_id),
    )
    setSelectedNumbers(restoredNumbers.filter(Boolean) as IClinicNumber[])
    setActiveNumber(null)
  }, [activeNumber, selectedNumbers, clinicNumbers])

  useEffect(() => {
    if (initialFlow) {
      const def = parseDefinition(initialFlow.definition as unknown)
      const linkedIds = resolveFlowClinicNumberIds(initialFlow)
      const definition =
        linkedIds.length > 0 ? { ...def, numbers: linkedIds } : def
      setCallFlow({ ...initialFlow, definition })
      setBaselineName(initialFlow.name)
    }
  }, [initialFlow])

  useEffect(() => {
    if (initialFlow) {
      setSelectedNumbers(
        resolveSelectedNumbersFromFlow(initialFlow, clinicNumbers),
      )
    }
  }, [initialFlow, clinicNumbers])

  const resetDraft = useCallback(() => {
    setEditingStep(null)
    pendingAudioByStepIdRef.current.clear()
    setPendingAudioRevision((n) => n + 1)
    if (!callFlow?.call_flow_id) {
      setCallFlow({
        ...defaultFlow,
        clinic_id: clinicId,
        flow_type: initialFlow?.flow_type ?? callFlow.flow_type ?? 'call',
      })
      setSelectedNumbers([])
      setActiveNumber(null)
      return
    }
    if (initialFlow) {
      const def = parseDefinition(initialFlow.definition as unknown)
      const linkedIds = resolveFlowClinicNumberIds(initialFlow)
      const definition =
        linkedIds.length > 0 ? { ...def, numbers: linkedIds } : def
      setCallFlow({ ...initialFlow, name: baselineName, definition })
      setSelectedNumbers(
        resolveSelectedNumbersFromFlow(initialFlow, clinicNumbers),
      )
      setActiveNumber(null)
    }
  }, [initialFlow, callFlow, clinicId, clinicNumbers, baselineName])

  const refreshList = useCallback(async () => {
    if (!clinicId) return
    try {
      await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
      await dispatch(fetchCallFlowsAction(clinicId)).unwrap()
    } catch {
      showToast(createFailedToast('Error', 'Could not load call flows.'))
    }
  }, [clinicId, dispatch, showToast])

  const registerPendingAudioFile = useCallback(
    (stepId: string, file: File | null) => {
      if (file) pendingAudioByStepIdRef.current.set(stepId, file)
      else pendingAudioByStepIdRef.current.delete(stepId)
      setPendingAudioRevision((n) => n + 1)
    },
    [],
  )

  const saveFlow = useCallback(async () => {
    if (!clinicId) return
    showLoading({ container: LOADING_CONTAINER })
    try {
      const pending = pendingAudioByStepIdRef.current
      const hasPendingAudio = pending.size > 0

      let definition = callFlow.definition
      const selectedNumberIds = selectedNumbers
        .map((n) => n.clinic_number_id)
        .filter((id): id is string => Boolean(id))
      definition = stripSmsMessages({
        ...definition,
        numbers: selectedNumberIds,
      })
      const flowType = callFlow.flow_type ?? 'call'
      const buildStatus = (def: ICallFlowDefinition): 'active' | 'draft' =>
        callFlowDefinitionFullyConfigured(def) ? 'active' : 'draft'

      if (!callFlow.call_flow_id) {
        const created = await dispatch(
          createCallFlowAction({
            clinic_id: clinicId,
            name: callFlow.name,
            flow_type: flowType,
            status: buildStatus(definition),
            definition: flowToPersistDefinition(definition),
            clinic_number_ids: selectedNumberIds,
          }),
        ).unwrap()
        const createdFlowId = created.call_flow_id
        if (!createdFlowId) {
          showToast(createFailedToast('Error', 'Could not save call flow.'))
          return
        }

        if (hasPendingAudio) {
          try {
            const pendingCopy = new Map(pending)
            definition = await uploadPendingAudioInDefinition(
              callFlow.definition,
              pendingCopy,
              clinicId,
              createdFlowId,
            )
            pending.clear()
          } catch (e) {
            setCallFlow((prev) => ({
              ...prev,
              call_flow_id: createdFlowId,
            }))
            showToast(
              createFailedToast(
                'Error',
                'Call flow draft was created, but audio upload failed. Please retry save.',
              ),
            )
            return
          }

          await dispatch(
            updateCallFlowAction({
              id: createdFlowId,
              body: {
                name: callFlow.name,
                status: buildStatus(definition),
                definition: flowToPersistDefinition(definition),
                clinic_number_ids: selectedNumberIds,
              },
            }),
          ).unwrap()
        }
      } else {
        if (hasPendingAudio) {
          const deleted = await deleteBlobPrefix(
            CALL_FLOW_AUDIO_FOLDER,
            callFlow.call_flow_id,
            clinicId,
          )
          if (deleted.statusCode !== 200) {
            showToast(
              createFailedToast(
                'Error',
                deleted.message || 'Could not clear existing call flow audio.',
              ),
            )
            return
          }
          try {
            const pendingCopy = new Map(pending)
            definition = await uploadPendingAudioInDefinition(
              callFlow.definition,
              pendingCopy,
              clinicId,
              callFlow.call_flow_id,
            )
            pending.clear()
          } catch (e) {
            showToast(
              createFailedToast('Error', 'Could not upload call flow audio.'),
            )
            return
          }
        }
        await dispatch(
          updateCallFlowAction({
            id: callFlow.call_flow_id,
            body: {
              name: callFlow.name,
              status: buildStatus(definition),
              definition: flowToPersistDefinition(definition),
              clinic_number_ids: selectedNumberIds,
            },
          }),
        ).unwrap()
      }
      setCallFlow((prev) => ({ ...prev, definition }))
      showToast(createSuccessToast('Saved', 'Call flow saved.'))
      await refreshList()
      cancelEditor()
    } catch {
      showToast(createFailedToast('Error', 'Could not save call flow.'))
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    callFlow,
    clinicId,
    dispatch,
    showLoading,
    hideLoading,
    showToast,
    refreshList,
    cancelEditor,
    selectedNumbers,
  ])

  const placeStepPickerAtAnchor = useCallback((el: HTMLElement | null) => {
    const w = 220
    const gap = 14
    const edgePad = 8
    // 8 step tiles, 2 columns, min tile height 96px + grid gaps + shell padding
    const pickerHeight = 436

    if (!el) {
      setPickerPos({
        top: 120,
        left: 24,
        placement: 'below',
      })
      return
    }

    const r = el.getBoundingClientRect()
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1920
    const vh = typeof window !== 'undefined' ? window.innerHeight : 1080
    const host =
      diagramWrapRef.current?.closest('.scp-call-flow-editor-wrapper') ??
      diagramWrapRef.current
    const hostRect = host?.getBoundingClientRect()
    const minTop = (hostRect?.top ?? 0) + edgePad
    const maxBottom = (hostRect?.bottom ?? vh) - edgePad
    const minLeft = edgePad
    const maxLeft = vw - w - edgePad

    const spaceBelow = maxBottom - r.bottom - gap
    const spaceAbove = r.top - gap - minTop
    const fitsBelow = spaceBelow >= pickerHeight
    const fitsAbove = spaceAbove >= pickerHeight

    let placement: 'above' | 'below'
    if (fitsBelow) {
      placement = 'below'
    } else if (fitsAbove) {
      placement = 'above'
    } else {
      placement = spaceAbove > spaceBelow ? 'above' : 'below'
    }

    let top =
      placement === 'above' ? r.top - gap - pickerHeight : r.bottom + gap

    top = Math.max(minTop, Math.min(top, maxBottom - pickerHeight))

    setPickerPos({
      top,
      left: Math.min(
        Math.max(minLeft, r.left + r.width / 2 - w / 2 + 6),
        maxLeft,
      ),
      placement,
    })
  }, [])

  const placeStepPicker = useCallback(
    (branch: CallFlowBranchKey) => {
      placeStepPickerAtAnchor(addStepBtnRefs.current[branch])
    },
    [placeStepPickerAtAnchor],
  )

  const placeDialOutcomePicker = useCallback(
    (ctx: PickerDialContext) => {
      const key = dialOutcomeAnchorRefKey(
        ctx.branch,
        ctx.dialStepId,
        ctx.outcome,
      )
      placeStepPickerAtAnchor(dialOutcomeBtnRefs.current[key])
    },
    [placeStepPickerAtAnchor],
  )

  const onAddStep = useCallback(
    (branch: CallFlowBranchKey) => {
      setPickerDialContext(null)
      setPickerBranch(branch)
      setPickerOpen(true)
      placeStepPicker(branch)
    },
    [placeStepPicker],
  )

  const onAddDialOutcomeStep = useCallback(
    (
      branch: CallFlowBranchKey,
      dialStepId: string,
      outcome: DialFollowOutcome,
    ) => {
      const ctx: PickerDialContext = { branch, dialStepId, outcome }
      setPickerBranch(branch)
      setPickerDialContext(ctx)
      setPickerOpen(true)
      placeDialOutcomePicker(ctx)
    },
    [placeDialOutcomePicker],
  )

  const updateActiveNumber = useCallback(
    (data: Partial<IClinicNumber & { timezone?: string }>) => {
      if (!activeNumber) return
      const currentIndex = activeNumber.clinic_number_id
        ? selectedNumbers.findIndex(
            (n) => n.clinic_number_id === activeNumber.clinic_number_id,
          )
        : -1
      if (data.timezone) {
        const newActiveNumber = {
          ...activeNumber,
          ...data,
          workingHours: (data.workingHours ?? activeNumber.workingHours)?.map(
            (wh) => ({ ...wh, timezone: data.timezone }),
          ),
        }
        const newSelectedNumbers = selectedNumbers.map((n) => ({
          ...n,
          workingHours: n.workingHours?.map((wh) => ({
            ...wh,
            timezone: data.timezone,
          })),
        }))
        if (currentIndex >= 0) {
          newSelectedNumbers[currentIndex] = newActiveNumber
        }
        setActiveNumber(newActiveNumber)
        setSelectedNumbers(newSelectedNumbers)
        return
      }
      if (currentIndex < 0) {
        setActiveNumber({ ...activeNumber, ...data })
        return
      }
      const newActiveNumber = { ...activeNumber, ...data }
      const newSelectedNumbers = [...selectedNumbers]
      newSelectedNumbers[currentIndex] = newActiveNumber
      setActiveNumber(newActiveNumber)
      setSelectedNumbers(newSelectedNumbers)
    },
    [activeNumber, selectedNumbers],
  )

  const onClinicNumbersSaved = useCallback((updated: IClinicNumber[]) => {
    const byId = new Map(
      updated
        .filter((n) => n.clinic_number_id)
        .map((n) => [n.clinic_number_id!, n]),
    )
    setSelectedNumbers((prev) =>
      prev.map((n) => {
        if (!n.clinic_number_id) return n
        const next = byId.get(n.clinic_number_id)
        return next ? { ...n, ...next } : n
      }),
    )
    setActiveNumber((cur) => {
      if (!cur?.clinic_number_id) return cur
      const next = byId.get(cur.clinic_number_id)
      return next ? { ...cur, ...next } : cur
    })
  }, [])

  useEffect(() => {
    if (!pickerOpen || !pickerBranch) {
      return
    }
    const place = (): void => {
      if (pickerDialContext) {
        placeDialOutcomePicker(pickerDialContext)
      } else {
        placeStepPicker(pickerBranch)
      }
    }
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [
    pickerOpen,
    pickerBranch,
    pickerDialContext,
    placeStepPicker,
    placeDialOutcomePicker,
  ])

  useEffect(() => {
    if (!pickerOpen) {
      setPickerPos(null)
    }
  }, [pickerOpen])

  const saveName = useCallback(async () => {
    if (!clinicId || !callFlow.call_flow_id) {
      setEditName(false)
      return
    }
    showLoading({ container: LOADING_CONTAINER })
    try {
      const saved = await dispatch(
        updateCallFlowAction({
          id: callFlow.call_flow_id,
          body: {
            name: callFlow.name,
          },
        }),
      ).unwrap()
      setBaselineName(saved.name)
      setCallFlow((current) => ({ ...current, name: saved.name }))
      setEditName(false)
      showToast(createSuccessToast('Saved', 'Call flow saved.'))
    } catch {
      showToast(createFailedToast('Error', 'Could not save call flow.'))
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [callFlow, clinicId, dispatch, showLoading, hideLoading, showToast])

  const cancelEditName = useCallback(() => {
    setCallFlow((current) => ({ ...current, name: baselineName }))
    setEditName(false)
  }, [baselineName])

  const closeStepPicker = useCallback(() => {
    setPickerOpen(false)
    setPickerBranch(null)
    setPickerDialContext(null)
    setPickerPos(null)
  }, [])

  useEffect(() => {
    if (!pickerOpen) return
    const onDocMouseDown = (e: MouseEvent): void => {
      const t = e.target as Node | null
      if (!t) return
      const el =
        t instanceof Element ? t : t instanceof Text ? t.parentElement : null
      if (el?.closest('.scp-call-flow-step-picker')) return
      closeStepPicker()
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [pickerOpen, closeStepPicker])

  const closeStepPanel = useCallback(() => {
    setEditingStep(null)
  }, [])

  const openStepPanel = useCallback(
    (branch: CallFlowBranchKey, stepId: string) => {
      setEditingStep({ branch, stepId })
    },
    [],
  )

  const updateStep = useCallback(
    (
      branch: CallFlowBranchKey,
      stepId: string,
      patch: Partial<ICallFlowStep>,
    ) => {
      setCallFlow((prev) => {
        const branchSteps = prev.definition.branches[branch]?.steps ?? []
        const topIdx = branchSteps.findIndex((s) => s.id === stepId)
        if (topIdx >= 0) {
          const nextSteps = branchSteps.map((s) =>
            s.id === stepId ? ({ ...s, ...patch } as ICallFlowStep) : s,
          )
          return {
            ...prev,
            definition: {
              ...prev.definition,
              branches: {
                ...prev.definition.branches,
                [branch]: { steps: nextSteps },
              },
            },
          }
        }
        const nextSteps = branchSteps.map((s) => {
          if (s.type !== 'dial_number') return s
          const d = s
          if (d.answeredFollowStep?.id === stepId) {
            return {
              ...d,
              answeredFollowStep: {
                ...d.answeredFollowStep,
                ...patch,
              } as ICallFlowStep,
            } as ICallFlowStep
          }
          if (d.noAnswerFollowStep?.id === stepId) {
            return {
              ...d,
              noAnswerFollowStep: {
                ...d.noAnswerFollowStep,
                ...patch,
              } as ICallFlowStep,
            } as ICallFlowStep
          }
          return s
        })
        return {
          ...prev,
          definition: {
            ...prev.definition,
            branches: {
              ...prev.definition.branches,
              [branch]: { steps: nextSteps },
            },
          },
        }
      })
    },
    [],
  )

  const addStepToBranch = useCallback(
    (type: CallFlowStepType) => {
      if (!pickerBranch) return
      if (pickerDialContext) {
        if (type === 'dial_number') return
        const { branch, dialStepId, outcome } = pickerDialContext
        const step = createStep(type)
        setEditingStep({
          branch,
          stepId: step.id,
          dialParentStepId: dialStepId,
          dialOutcome: outcome,
        })
        setCallFlow((prev) => {
          const branchSteps = prev.definition.branches[branch]?.steps ?? []
          const nextSteps = branchSteps.map((s) => {
            if (s.id !== dialStepId || s.type !== 'dial_number') return s
            return (
              outcome === 'answered'
                ? { ...s, answeredFollowStep: step }
                : { ...s, noAnswerFollowStep: step }
            ) as ICallFlowStep
          })
          return {
            ...prev,
            definition: {
              ...prev.definition,
              branches: {
                ...prev.definition.branches,
                [branch]: { steps: nextSteps },
              },
            },
          }
        })
        closeStepPicker()
        return
      }
      const step = createStep(type)
      setEditingStep({ branch: pickerBranch, stepId: step.id })
      setCallFlow((prev) => ({
        ...prev,
        definition: {
          ...prev.definition,
          branches: {
            ...prev.definition.branches,
            [pickerBranch]: {
              steps: [
                ...(prev.definition.branches[pickerBranch]?.steps ?? []),
                step,
              ],
            },
          },
        },
      }))
      closeStepPicker()
    },
    [pickerBranch, pickerDialContext, closeStepPicker],
  )

  const removeStepFromBranch = useCallback(
    (branch: CallFlowBranchKey, stepId: string) => {
      setEditingStep((cur) => {
        if (!cur) return null
        if (cur.branch !== branch) return cur
        if (cur.stepId === stepId) return null
        return cur
      })
      setCallFlow((prev) => {
        const branchSteps = prev.definition.branches[branch]?.steps ?? []
        const filtered = branchSteps.filter((s) => s.id !== stepId)
        if (filtered.length !== branchSteps.length) {
          return {
            ...prev,
            definition: {
              ...prev.definition,
              branches: {
                ...prev.definition.branches,
                [branch]: { steps: filtered },
              },
            },
          }
        }
        const nextSteps = branchSteps.map((s) => {
          if (s.type !== 'dial_number') return s
          const d = s
          if (d.answeredFollowStep?.id === stepId) {
            return { ...d, answeredFollowStep: undefined } as ICallFlowStep
          }
          if (d.noAnswerFollowStep?.id === stepId) {
            return { ...d, noAnswerFollowStep: undefined } as ICallFlowStep
          }
          return s
        })
        return {
          ...prev,
          definition: {
            ...prev.definition,
            branches: {
              ...prev.definition.branches,
              [branch]: { steps: nextSteps },
            },
          },
        }
      })
    },
    [],
  )

  const findOtherCallFlowOwningNumber = useCallback(
    (clinicNumberId: string): ICallFlow | null => {
      const editingId = callFlow.call_flow_id
      const flowType = callFlow.flow_type ?? 'call'
      for (const flow of callFlows) {
        if (flow.call_flow_id && flow.call_flow_id === editingId) continue
        if ((flow.flow_type ?? 'call') !== flowType) continue
        if (
          flow.number_links?.some((l) => l.clinic_number_id === clinicNumberId)
        ) {
          return flow
        }
      }
      return null
    },
    [callFlows, callFlow.call_flow_id, callFlow.flow_type],
  )

  const applyAddNumberToSelection = useCallback((number: IClinicNumber) => {
    if (!number.clinic_number_id) return
    setEditingStep(null)
    const id = number.clinic_number_id
    setSelectedNumbers((prev) => {
      if (prev.some((n) => n.clinic_number_id === id)) return prev
      return [...prev, number]
    })
    setCallFlow((prev) => {
      if (prev.definition.numbers.includes(id)) return prev
      return {
        ...prev,
        definition: {
          ...prev.definition,
          numbers: [...prev.definition.numbers, id],
        },
      }
    })
    setActiveNumber((cur) => cur ?? number)
  }, [])

  const confirmNumberReassign = useCallback(() => {
    if (!numberReassignConfirm) return
    applyAddNumberToSelection(numberReassignConfirm.number)
    setNumberReassignConfirm(null)
  }, [numberReassignConfirm, applyAddNumberToSelection])

  const cancelNumberReassign = useCallback(() => {
    const cur = numberReassignConfirmRef.current
    setNumberReassignConfirm(null)
    const id = cur?.number.clinic_number_id
    if (!id) return
    setSelectedNumbers((prev) => prev.filter((n) => n.clinic_number_id !== id))
    setCallFlow((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        numbers: prev.definition.numbers.filter((n) => n !== id),
      },
    }))
    setActiveNumber((a) => (a?.clinic_number_id === id ? null : a))
    setNumberListCheckboxRemountKey((m) => ({
      ...m,
      [id]: (m[id] ?? 0) + 1,
    }))
  }, [])

  const handleSelectNumber = useCallback(
    (e: { checked: boolean }, number: IClinicNumber) => {
      if (!number.clinic_number_id) return
      if (e.checked) {
        const other = findOtherCallFlowOwningNumber(number.clinic_number_id)
        if (other) {
          setNumberReassignConfirm({
            number,
            otherFlowName: other.name,
          })
          return
        }
        applyAddNumberToSelection(number)
      } else {
        setSelectedNumbers((prev) =>
          prev.filter((n) => n.clinic_number_id !== number.clinic_number_id),
        )
        setCallFlow((prev) => ({
          ...prev,
          definition: {
            ...prev.definition,
            numbers: prev.definition.numbers.filter(
              (n) => n !== number.clinic_number_id!,
            ),
          },
        }))
        if (activeNumber?.clinic_number_id === number.clinic_number_id) {
          if (selectedNumbers.length > 1) {
            setActiveNumber(
              clinicNumbers.find(
                (n) =>
                  n.clinic_number_id === selectedNumbers[1]?.clinic_number_id,
              ) ?? null,
            )
          } else {
            setActiveNumber(null)
          }
        }
      }
    },
    [
      findOtherCallFlowOwningNumber,
      applyAddNumberToSelection,
      activeNumber,
      selectedNumbers,
      clinicNumbers,
    ],
  )

  useEffect(() => {
    if (!numberListOpen) return
    const onDocMouseDown = (e: MouseEvent): void => {
      if (numberReassignConfirm) return
      const t = e.target as Node | null
      if (!t) return
      const el =
        t instanceof Element ? t : t instanceof Text ? t.parentElement : null
      if (el?.closest('.modal, .modal-backdrop')) return
      if (numberListFlyoutRef.current?.contains(t)) return
      if (diagramWrapRef.current?.contains(t)) return
      setNumberListOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [numberListOpen, numberReassignConfirm])

  return {
    activeNumber,
    addStepBtnRefs,
    dialOutcomeBtnRefs,
    addStepToBranch,
    branches,
    callFlow,
    cancelEditName,
    cancelWorkingHours,
    closeStepPanel,
    clinicNumbers,
    internalExtensions,
    closeStepPicker,
    diagramWrapRef,
    editName,
    editingStep,
    handleSelectNumber,
    numberReassignConfirm,
    confirmNumberReassign,
    cancelNumberReassign,
    numberListCheckboxRemountKey,
    numberListOpen,
    numberListFlyoutRef,
    onAddStep,
    onAddDialOutcomeStep,
    onClinicNumbersSaved,
    openStepPanel,
    removeStepFromBranch,
    pickerOpen,
    pickerDialContext,
    pickerPos,
    resetDraft,
    saveFlow,
    registerPendingAudioFile,
    saveName,
    selectedNumbers,
    setActiveNumber,
    setCallFlow,
    setEditName,
    setEditingStep,
    setHeaderButtons,
    setHeaderTitle,
    setNumberListOpen,
    showCallFlowBranches,
    updateActiveNumber,
    updateStep,
    workingHoursDataDirty,
    workingHoursPhoneLabel,
    goToNextWorkingHoursNumber,
    hasChanges,
  }
}

export default useFlowEditor
